import Bugsnag from "@bugsnag/js";
import { client } from "@nju/http-client";
import { BasicError, err, ok } from "@nju/result";
import { cutPhoneNumberPrefix } from "@nju/utils";
import { IFetchOrchestratorResult } from "./types";
import type {
  IChangeMsisdnOrchestrator,
  IChangeSimEsimOrchestrator,
  IFSecureOrchestrator,
  IFsecureReactivationOrchestrator,
  IFtthOrchestrator,
  IFtthReactivationOrchestrator,
  IGroupOrchestrator,
  IMobileOrchestrator,
  IMobileReactivationOrchestrator,
  IModemReturnOrchestrator,
  IMultiOrderOrchestrator,
  ITvOrchestrator,
} from "./validators";
import { orchestrators } from "./validators";
import { verificationErrorResponse } from "./validators/combineOrchestrators";

export async function fetchOrchestrators(
  includeAdminMembers?: boolean,
  onlyInProgress?: boolean,
): Promise<IFetchOrchestratorResult> {
  const result = await client(
    `${process.env.NEXT_PUBLIC_PUBLIC_GATEWAY_HOST}/orchestrators`,
    {
      validator: orchestrators,
      headers: {
        version: "2",
        apikey: process.env.NEXT_PUBLIC_APIKEY || "unknown",
      },
      searchParams: {
        ...(includeAdminMembers && { includeAdminMembers }),
        ...(onlyInProgress && { onlyInProgress }),
      },
    },
  );
  if (result.isError) {
    Bugsnag.notify(result.err);

    const errorInfo = verificationErrorResponse.safeParse(
      result.err.extra?.json,
    );

    if (errorInfo.success && errorInfo.data.code === "50193") {
      return err(
        new BasicError({
          message: "orchestrator in use by other agent",
          code: "CLIENT_LOCKED_BY_OTHER_AGENT",
        }),
      );
    }

    return result;
  }

  const ftth = result.value.find(({ type }) => {
    return type === "NJU_FTTH_ONBOARDING";
  }) as IFtthOrchestrator | undefined;

  const fsecure = result.value.find(({ type }) => {
    return type === "NJU_FSECURE_ONBOARDING";
  }) as IFSecureOrchestrator | undefined;

  const modemReturn = result.value.find(({ type }) => {
    return type === "NJU_FTTH_MODEM_RETURN";
  }) as IModemReturnOrchestrator | undefined;

  const ftthReactivation = result.value.find(({ type }) => {
    return type === "NJU_FTTH_REACTIVATION";
  }) as IFtthReactivationOrchestrator | undefined;

  const fsecureReactivation = result.value.find(({ type }) => {
    return type === "NJU_FSECURE_REACTIVATION";
  }) as IFsecureReactivationOrchestrator | undefined;

  const mobileReactivation = result.value.find(({ type }) => {
    return type === "NJU_MOBILE_REACTIVATION";
  }) as IMobileReactivationOrchestrator | undefined;

  const mobile = result.value.find(({ type }) => {
    return type === "NJU_MOBILE_ONBOARDING";
  }) as IMobileOrchestrator | undefined;

  const mobiles = result.value.filter(({ type }) => {
    return type === "NJU_MOBILE_ONBOARDING";
  }) as IMobileOrchestrator[] | undefined;

  const changeMsisdn = result.value.find(({ type }) => {
    return type === "NJU_CHANGE_MSISDN";
  }) as IChangeMsisdnOrchestrator | undefined;

  const group = result.value.find(({ type }) => {
    return type === "NJU_GROUP_ONBOARDING";
  }) as IGroupOrchestrator | undefined;

  const tv = result.value.find(({ type }) => {
    return type === "NJU_TV_SERVICE";
  }) as ITvOrchestrator | undefined;

  const multiOrder = result.value.find(({ type }) => {
    return type === "NJU_MULTIORDER_ONBOARDING";
  }) as IMultiOrderOrchestrator | undefined;

  const changeSimEsim = result.value.find(({ type }) => {
    return type === "NJU_SIM_MANAGEMENT";
  }) as IChangeSimEsimOrchestrator | undefined;

  return ok({
    ftth: ftth && {
      ...ftth,
      /*
       * for now we have only 1 offer, but data structure is array, so I guess in the future we will have to
       * deal with many offer ids, but for now it's not necessary so for better management we treat it as a string
       **/
      offerId: ftth.offerIds[0],
      contactNumberWithoutCountyCode: cutPhoneNumberPrefix(ftth.contactNumber),
    },
    fsecure: fsecure && {
      ...fsecure,
      offerId: fsecure.offerIds[0],
    },
    modemReturn,
    ftthReactivation,
    fsecureReactivation,
    mobileReactivation: mobileReactivation && {
      ...mobileReactivation,
      isFreeFromActivationFee:
        mobileReactivation.orderSummary?.totalOneTimeOrderPrice === 0,
    },
    mobile: mobile && {
      ...mobile,
      offerId: mobile.offerIds[0],
      contactNumberWithoutCountyCode: cutPhoneNumberPrefix(
        mobile.contactNumber,
      ),
      isFreeFromActivationFee:
        mobile.orderSummary?.totalOneTimeOrderPrice === 0,
    },
    mobiles,
    changeMsisdn,
    group,
    tv,
    changeSimEsim,
    multiOrder: multiOrder && {
      ...multiOrder,
      offerId: multiOrder.offerIds[0],
      hasNewNumberOrMnp: multiOrder.multiOrderItems?.some((item) => {
        return item.processId === "NEW_NUMBER" || item.processId === "MNP_IN";
      }),
      numberOfMigratingMsisdns: multiOrder.multiOrderItems?.filter((item) => {
        return item.processId === "LEGACY_MIGRATION";
      }).length,
    },
  });
}
